import { useEffect, useRef } from 'react';
import { AimOutlined } from '@ant-design/icons';
import { theme } from 'antd';
import type { CobradorCercaClienteRow } from '../api/cobrador-service';
import type { LeafletModule } from '../../../lib/useLeaflet';
import { useLeaflet } from '../../../lib/useLeaflet';
import { useT } from '../../../locales';
import { zoomParaRadioCerca } from '../lib/cerca';
import type { CercaUbicacion } from './useCercaUbicacion';

type LeafletMap = import('leaflet').Map;
type LeafletMarker = import('leaflet').Marker;

const MAP_MAX_ZOOM = 19;
const MAP_FOCUS_ZOOM = 16;
const MAP_MARKER_SIZE = 20;
const MAP_MARKER_SIZE_SELECTED = 30;
const COLECCIONISTA_MARKER_SIZE = 26;
const MAPER_WHITE = '#fff';
const MARKER_SHADOW = '0 1px 4px rgba(0,0,0,0.5)';

interface CercaMapaProps {
  ubicacion: CercaUbicacion;
  radioMetros: number;
  clientes: CobradorCercaClienteRow[];
  selectedClienteId: number | null;
  onSelect: (cliente: CobradorCercaClienteRow) => void;
}

function iconoColeccionista(L: LeafletModule, borderColor: string, fillColor: string) {
  const size = COLECCIONISTA_MARKER_SIZE;
  return L.divIcon({
    className: '',
    html: `<div style="
      width: ${size}px; height: ${size}px; border-radius: 50%;
      background: ${fillColor}; border: 3px solid ${borderColor};
      box-shadow: ${MARKER_SHADOW}, 0 0 0 4px rgba(0,0,0,0.12);
      box-sizing: border-box;
    "></div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
}

function iconoCliente(L: LeafletModule, seleccionado: boolean, token: ReturnType<typeof theme.useToken>['token']) {
  const size = seleccionado ? MAP_MARKER_SIZE_SELECTED : MAP_MARKER_SIZE;
  const fill = seleccionado ? token.colorSuccess : token.colorWarning;
  return L.divIcon({
    className: '',
    html: `<div style="
      width: ${size}px; height: ${size}px; border-radius: 50%;
      background: ${fill}; border: 3px solid ${MAPER_WHITE};
      box-shadow: ${MARKER_SHADOW};
      box-sizing: border-box;
    "></div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
}

/**
 * Mapa del tab Cerca: marcador del cobrador (snapshot), círculo del radio y
 * marcadores de clientes pendientes con coordenadas. Carga Leaflet de forma
 * diferida vía useLeaflet, igual que el resto de mapas del sistema.
 */
export function CercaMapa({
  ubicacion,
  radioMetros,
  clientes,
  selectedClienteId,
  onSelect,
}: CercaMapaProps) {
  const t = useT();
  const { token } = theme.useToken();
  const L = useLeaflet();
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<LeafletMap | null>(null);
  const markersRef = useRef<Map<number, LeafletMarker>>(new Map());
  const onSeleccionRef = useRef(onSelect);
  const traducerRef = useRef(t);

  onSeleccionRef.current = onSelect;
  traducerRef.current = t;

  useEffect(() => {
    if (!L || !mapRef.current) return;

    const existente = mapInstanceRef.current;
    if (existente) {
      existente.remove();
      mapInstanceRef.current = null;
    }

    const [lat, lng] = [ubicacion.lat, ubicacion.lng];
    const zoom = zoomParaRadioCerca(radioMetros);
    const map = L.map(mapRef.current, {
      center: [lat, lng],
      zoom,
      zoomControl: true,
      maxZoom: MAP_MAX_ZOOM,
    });

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap contributors',
      maxZoom: MAP_MAX_ZOOM,
    }).addTo(map);

    const radio = L.circle([lat, lng], {
      radius: radioMetros,
      color: token.colorPrimary,
      weight: 1.5,
      opacity: 0.6,
      fillColor: token.colorPrimary,
      fillOpacity: 0.08,
    });
    radio.addTo(map);

    const me = L.marker([lat, lng], {
      icon: iconoColeccionista(L, MAPER_WHITE, token.colorPrimary),
      title: traducerRef.current('pages.cobrador.cerca.coleccionista'),
      zIndexOffset: 500,
    });
    me.addTo(map);

    markersRef.current.clear();
    for (const cliente of clientes) {
      const marker = L.marker(
        [cliente.ubicacion.latitud, cliente.ubicacion.longitud],
        {
          icon: iconoCliente(L, cliente.idCliente === selectedClienteId, token),
          title:
            cliente.nombre ??
            traducerRef.current('dashboard.rutaCobros.clientFallback', { id: cliente.idCliente }),
        },
      );
      marker.bindTooltip(
        cliente.nombre ??
          traducerRef.current('dashboard.rutaCobros.clientFallback', {
            id: cliente.idCliente,
          }),
        { direction: 'top', offset: [0, -8] },
      );
      marker.on('click', () => onSeleccionRef.current(cliente));
      marker.addTo(map);
      markersRef.current.set(cliente.idCliente, marker);
    }

    mapInstanceRef.current = map;
    if (clientes.length === 0) {
      map.setView([lat, lng], zoom);
    }

    return () => {
      map.remove();
      mapInstanceRef.current = null;
      markersRef.current.clear();
    };
  }, [L, ubicacion.lat, ubicacion.lng, radioMetros, clientes, selectedClienteId, token.colorPrimary, token.colorSuccess, token.colorWarning]);

  // Focus del selector: re-dibuja el icono destacado, centra el mapa y
  // abre el tooltip del cliente seleccionado (misma mecánica que RutaCobrosMap).
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map || !L || selectedClienteId === null) return;

    const marker = markersRef.current.get(selectedClienteId);
    const cliente = clientes.find((c) => c.idCliente === selectedClienteId);
    if (!marker || !cliente) return;

    marker.setIcon(iconoCliente(L, true, token));
    map.setView([cliente.ubicacion.latitud, cliente.ubicacion.longitud], MAP_FOCUS_ZOOM);
    marker.openTooltip();
  }, [L, selectedClienteId, clientes, token.colorPrimary, token.colorSuccess, token.colorWarning]);

  // "Center on me": re-centers the map on the collector marker without
  // changing the zoom (the map instance ref is null once the map unmounts).
  const centrar = () => {
    const map = mapInstanceRef.current;
    map?.panTo([ubicacion.lat, ubicacion.lng]);
  };

  return (
    <div className="cerca-mapa-wrap">
      <div
        ref={mapRef}
        data-testid="cerca-mapa"
        role="region"
        aria-label={t('pages.cobrador.cerca.mapaTitle')}
        aria-busy={L === null}
        className="cerca-mapa"
        style={{
          isolation: 'isolate',
          ['--cerca-mapa-border' as string]: token.colorBorderSecondary,
          ['--mapa-popup-bg' as string]: token.colorBgContainer,
          ['--mapa-popup-text' as string]: token.colorText,
          ['--mapa-popup-border' as string]: token.colorBorderSecondary,
        }}
      />
      <button
        type="button"
        className="cerca-mapa__centrar"
        aria-label={t('pages.cobrador.cerca.centrar')}
        onClick={centrar}
      >
        <AimOutlined />
      </button>
    </div>
  );
}
